import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "./api";
import { clientId } from "./client-id";
import { debugFrontendXhrRequest } from "./development-debug-log";
import { readFileHandoff, removeFileHandoff } from "./file-handoff";
import type { ExistingPptDesignMode, GenerationMode, ImageGenerationStrategy, ImageStylePreferenceStyle, InputKind, NewPptDraft, PptTaskDetail } from "./outline-types";
import { CHINA_AI_MODEL_OPTIONS, chinaAiModelName, normalizeChinaAiModel, type ChinaAiModel } from "./ai-model-options";
import { fileTitle, isSupportedImageFile, isSupportedSourceFile, mimeTypeForFile, SOURCE_FILE_ACCEPT, SOURCE_FILE_LABEL, sourceFileType } from "./source-files";
import { THEME_PREVIEW_IMAGES, themeOptionsForMode, type ThemeOption } from "./theme-options";
import { marketingStyleForCode } from "./marketing-styles";
import { projectGenerationPath } from "./project-routes";
import { StylePreview } from "./style-selector";
import { Button, Icon } from "./ui";
import { notifyCreditActionSubmitted, notifyInsufficientCredits, requestCreditSpend } from "./credit-interactions";
import {
  DEFAULT_PPT_AUDIENCE,
  PPT_AUDIENCE_AUTO_VALUE,
  PPT_AUDIENCE_CUSTOM_VALUE,
  PPT_AUDIENCE_OPTIONS,
  normalizePptAudienceSelection,
  pptAudienceSelectValue,
} from "./ppt-audience-options";
import {
  DEFAULT_PPT_SCENE,
  PPT_SCENE_AUTO_VALUE,
  PPT_SCENE_CUSTOM_VALUE,
  PPT_SCENE_OPTIONS,
  normalizePptSceneSelection,
  pptSceneSelectValue,
} from "./ppt-scene-options";
import { useImageThemeOptions } from "./image-theme-catalog";
import { useUserAuth } from "./auth";
import { capabilityEnabled, featureEnabled } from "./feature-access";
import {
  FILE_SOURCE_TEXT_MAX_LENGTH,
  FILE_SOURCE_TEXT_MIN_LENGTH,
  normalizeFileSourceText,
  validateFileSourceTextLength,
} from "./ppt-text-limits";

const DRAFT_KEY = "pptdeep:new-ppt:draft:v5";
const LEGACY_DRAFT_KEYS = ["pptdeep:new-ppt:draft:v4", "pptdeep:new-ppt:draft:v3", "pptdeep:new-ppt:draft:v2", "pptdeep:new-ppt:draft:v1"] as const;
const GENERATION_MODE_OPTIONS: Array<{ key: GenerationMode; name: string; description: string }> = [
  { key: "standard", name: "标准模式", description: "DFAIPPT 主题版式，导出高清图片型 PPTX" },
  { key: "image", name: "图片模式", description: "每页生成整页图片，表现力优先" },
] as const;
const IMAGE_GENERATION_STRATEGY_OPTIONS: Array<{ key: ImageGenerationStrategy; name: string; description: string }> = [
  { key: "batch_preview", name: "批量预览", description: "先生成整套预览，确认后再生成逐页高清图片" },
  { key: "per_slide_hd", name: "主图高清", description: "逐页直接生成高清图片，并在生成页实时显示" },
] as const;
const DEFAULT_IMAGE_GENERATION_STRATEGY: ImageGenerationStrategy = "per_slide_hd";
const IMAGE_STYLE_OPTIONS: Array<{ value: ImageStylePreferenceStyle; label: string; description: string }> = [
  { value: "auto", label: "自动（写实优先）", description: "现实场景优先摄影，纯图表保持干净信息结构" },
  { value: "photorealistic", label: "写实摄影", description: "真实光影、材质、空间和镜头透视" },
  { value: "line_art", label: "线性描边", description: "统一线宽的克制单色描边插画" },
  { value: "flat_vector", label: "扁平矢量", description: "几何色块和有限色板的编辑插画" },
  { value: "hand_drawn", label: "手绘", description: "自然笔触和轻微不规则线条" },
  { value: "watercolor", label: "水彩", description: "透明晕染、柔和纸张与色彩过渡" },
  { value: "ink_wash", label: "水墨", description: "东方留白、墨色层次和克制强调色" },
  { value: "pencil_sketch", label: "铅笔素描", description: "石墨轮廓、排线明暗和纸张肌理" },
  { value: "clay_3d", label: "黏土 3D", description: "柔和棚拍光与统一微缩模型质感" },
  { value: "cartoon", label: "卡通", description: "统一比例、色块和角色语言" },
];
const EXISTING_PPT_DESIGN_MODE_OPTIONS: Array<{ value: ExistingPptDesignMode; label: string; description: string }> = [
  { value: "enhanced", label: "高质量增强", description: "保留逐页内容与信息结构，重新设计版式、层级和视觉表现" },
  { value: "premium", label: "素材锁定", description: "锁定原稿中的关键图片与素材，在新视觉系统中重新组织页面" },
  { value: "faithful", label: "保真美化", description: "最大限度保持原布局，仅优化配色、字体和页面细节" },
];
const DEVELOPMENT_PAGE_COUNT_OPTIONS = import.meta.env.DEV ? [
  { value: "2", label: "2 页" },
  { value: "5", label: "5 页" },
] : [];
const PAGE_COUNT_OPTIONS = [
  { value: "", label: "智能推荐", developmentOnly: false },
  ...DEVELOPMENT_PAGE_COUNT_OPTIONS,
  { value: "8", label: "8 页", developmentOnly: false },
  { value: "12", label: "12 页", developmentOnly: false },
  { value: "16", label: "16 页", developmentOnly: false },
  { value: "20", label: "20 页", developmentOnly: false },
];
const TONE_OPTIONS = [
  "一般",
  "专业",
  "正式",
  "非正式",
  "说服性",
  "鼓舞人心",
  "教学",
  "引人入胜",
  "分析性",
  "幽默",
  "叙述",
] as const;
type UploadPhase = "idle" | "selected" | "reading" | "uploading" | "parsing" | "parsed" | "error";
type UploadStatus = { phase: UploadPhase; percent: number; text: string };
type DraftAttachment = { id: string; file: File; title: string; previewUrl?: string };

const initialDraft: NewPptDraft = {
  sourceMode: "file",
  title: "",
  sourceText: "",
  originalName: "",
  aiModel: "deepseek",
  audienceMode: "auto",
  audience: DEFAULT_PPT_AUDIENCE,
  pageCount: "",
  deliveryPurpose: "balanced",
  narrativeMode: "briefing",
  visualStyle: "专业、克制、商务感",
  tone: TONE_OPTIONS[0],
  scene: DEFAULT_PPT_SCENE,
  sceneMode: "auto",
  customScene: "",
  beautifyMode: "preserve",
  existingPptDesignMode: "enhanced",
  existingPptImageResolution: "auto",
  generationMode: "standard",
  imageGenerationStrategy: DEFAULT_IMAGE_GENERATION_STRATEGY,
  imageStylePreference: "auto",
  requestedThemePack: "theme01",
  webResearchMode: "disabled",
};

export function NewPptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useUserAuth();
  const [draft, setDraft] = useState<NewPptDraft>(() => loadDraft(searchParams));
  const imageThemeOptions = useImageThemeOptions();
  const [file, setFile] = useState<File>();
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ phase: "idle", percent: 0, text: "" });
  const isChinaEdition = auth.me?.features?.edition === "china";
  const imageModeAvailable = featureEnabled(auth.me?.features, "renderer.image2");
  const existingPptBeautifyAvailable = imageModeAvailable
    && capabilityEnabled(auth.me?.features, "generation.existing_ppt_beautify");
  const testerMode = auth.me?.user.tier === "tester";
  const webResearchAvailable = testerMode && featureEnabled(auth.me?.features, "outline.web_research");
  const generationUploadLimitMb = auth.me?.uploadLimits?.maxTotalMb ?? 100;
  const generationUploadLimitBytes = generationUploadLimitMb * 1024 * 1024;
  const idempotencyKey = useRef(clientId());
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    LEGACY_DRAFT_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);
  useEffect(() => {
    const handoffId = searchParams.get("fileHandoff");
    if (!handoffId) return;
    let cancelled = false;
    readFileHandoff(handoffId)
      .then((handoffFiles) => {
        if (cancelled || !handoffFiles?.length) return;
        const sourceMode = searchParams.get("source") === "pptx" ? "pptx" : "file";
        if (sourceMode === "pptx" && (handoffFiles.length !== 1 || !handoffFiles[0].name.toLowerCase().endsWith(".pptx"))) {
          setError("官网带入的资料不符合 PPT 美化要求，请仅保留 1 个 PPTX 文件后重试。");
          void removeFileHandoff(handoffId).catch(() => undefined);
          return;
        }
        setDraft((current) => ({
          ...draftWithSourceMode(current, sourceMode),
          originalName: sourceMode === "pptx" ? handoffFiles[0].name : "",
          title: sourceMode === "pptx"
            ? fileTitle(handoffFiles[0].name)
            : current.title,
        }));
        if (sourceMode === "pptx") {
          setFile(handoffFiles[0]);
          setAttachments((current) => { clearAttachments(current); return []; });
        } else {
          setFile(undefined);
          setAttachments((current) => { clearAttachments(current); return handoffFiles.map(attachmentFromFile); });
        }
        setUploadStatus({ phase: "selected", percent: 0, text: sourceMode === "pptx" ? "官网文件已自动带入；点击开始美化后上传解析" : `官网已带入 ${handoffFiles.length} 个资料文件；点击生成后开始上传解析` });
        void removeFileHandoff(handoffId).catch(() => undefined);
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "首页上传文件读取失败，请重新选择。");
      });
    return () => { cancelled = true; };
  }, [searchParams]);
  useEffect(() => {
    if (!busy) return;
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [busy]);
  useEffect(() => {
    if (!isChinaEdition || imageModeAvailable) return;
    setFile(undefined);
    setDraft((current) => {
      if (current.sourceMode !== "pptx" && current.generationMode !== "image") return current;
      const sourceMode = current.sourceMode === "pptx" ? "file" : current.sourceMode;
      const options = themeOptionsForMode("standard", sourceMode);
      return {
        ...current,
        sourceMode,
        originalName: "",
        generationMode: "standard",
        requestedThemePack: defaultThemeKey("standard", options, initialDraft.requestedThemePack),
      };
    });
  }, [imageModeAvailable, isChinaEdition]);

  const update = <K extends keyof NewPptDraft>(key: K, value: NewPptDraft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const selectAudience = (value: string) => setDraft((current) => value === PPT_AUDIENCE_CUSTOM_VALUE
    ? { ...current, audienceMode: "custom", audience: "" }
    : value === PPT_AUDIENCE_AUTO_VALUE
      ? { ...current, audienceMode: "auto", audience: "" }
      : { ...current, audienceMode: "preset", audience: value });
  const selectScene = (value: string) => setDraft((current) => value === PPT_SCENE_CUSTOM_VALUE
    ? { ...current, sceneMode: "custom", scene: DEFAULT_PPT_SCENE, customScene: "" }
    : value === PPT_SCENE_AUTO_VALUE
      ? { ...current, sceneMode: "auto", scene: DEFAULT_PPT_SCENE, customScene: "" }
      : { ...current, sceneMode: "preset", scene: value, customScene: "" });
  const themeOptions = draft.generationMode === "image"
    ? imageThemeOptions
    : themeOptionsForMode(draft.generationMode, draft.sourceMode);
  const generationModeOptions = (draft.sourceMode === "pptx"
    ? GENERATION_MODE_OPTIONS.filter((mode) => mode.key === "image")
    : GENERATION_MODE_OPTIONS)
    .filter((mode) => !(isChinaEdition && mode.key === "image" && !imageModeAvailable))
    .map((mode) => ({
      ...mode,
      available: mode.key !== "image" || imageModeAvailable,
    }));
  const selectedTheme = themeOptions.find((theme) => theme.key === draft.requestedThemePack) || themeOptions[0];
  const showUploadProgress = ["reading", "uploading", "parsing", "parsed"].includes(uploadStatus.phase);
  const switchGenerationMode = (generationMode: GenerationMode) => {
    if (generationMode === "image" && !imageModeAvailable) {
      setError("图片模式当前未向此账户开放");
      return;
    }
    setDraft((current) => {
      const options = generationMode === "image"
        ? imageThemeOptions
        : themeOptionsForMode(generationMode, current.sourceMode);
      const requestedThemePack = options.some((theme) => theme.key === current.requestedThemePack)
        ? current.requestedThemePack
        : defaultThemeKey(generationMode, options, current.requestedThemePack);
      return {
        ...current,
        generationMode,
        requestedThemePack,
        ...(generationMode === "image" ? { imageGenerationStrategy: DEFAULT_IMAGE_GENERATION_STRATEGY } : {}),
      };
    });
  };
  const switchSource = (sourceMode: NewPptDraft["sourceMode"]) => {
    setFile(undefined); clearAttachments(attachments); setAttachments([]); setError("");
    setUploadStatus({ phase: "idle", percent: 0, text: "" });
    setDraft((current) => draftWithSourceMode(current, sourceMode));
  };
  const switchToExistingPptBeautify = () => {
    if (!existingPptBeautifyAvailable) {
      setError("现有 PPT 美化使用图片模式，当前未向此账户开放");
      return;
    }
    setError("");
    if (draft.sourceMode !== "pptx") {
      setFile(undefined);
      clearAttachments(attachments);
      setAttachments([]);
      setUploadStatus({ phase: "idle", percent: 0, text: "" });
    }
    setDraft((current) => draftWithExistingPptBeautify(current));
  };
  const chooseFile = (selected?: File) => {
    setError("");
    if (!selected) {
      setFile(undefined);
      setUploadStatus({ phase: "idle", percent: 0, text: "" });
      setDraft((current) => ({ ...current, originalName: "", title: "" }));
      return;
    }
    if (draft.sourceMode === "pptx" && !selected.name.toLowerCase().endsWith(".pptx")) {
      setFile(undefined);
      setDraft((current) => ({ ...current, originalName: "" }));
      setError("请选择 PPTX 文件。");
      setUploadStatus({ phase: "error", percent: 0, text: "文件类型不符合要求，请重新选择" });
      return;
    }
    if (draft.sourceMode === "file" && !isSupportedSourceFile(selected)) {
      setFile(undefined);
      setDraft((current) => ({ ...current, originalName: "" }));
      setError(`请选择支持的资料文件：${SOURCE_FILE_LABEL}。`);
      setUploadStatus({ phase: "error", percent: 0, text: "文件类型不支持，请重新选择" });
      return;
    }
    if (selected.size > 30 * 1024 * 1024) {
      setFile(undefined);
      setDraft((current) => ({ ...current, originalName: "" }));
      setError("文件不能超过 30 MB。");
      setUploadStatus({ phase: "error", percent: 0, text: "文件超过 30 MB，请重新选择" });
      return;
    }
    setFile(selected);
    setDraft((current) => ({ ...current, originalName: selected.name, title: fileTitle(selected.name) }));
    setUploadStatus({ phase: "selected", percent: 0, text: "已选择文件，已使用文件名作为标题；点击开始美化后上传解析" });
  };

  const chooseAttachments = (selected: FileList | null) => {
    setError("");
    if (!selected?.length) return;
    const incoming = Array.from(selected);
    const nextCount = attachments.length + incoming.length;
    if (nextCount > 10) return setError("最多添加 10 个附件。");
    const unsupported = incoming.find((item) => !isSupportedSourceFile(item));
    if (unsupported) {
      return setError(`不支持的附件：${unsupported.name}。支持 ${SOURCE_FILE_LABEL}。`);
    }
    const oversizedImage = incoming.find((item) => isSupportedImageFile(item) && item.size > 10 * 1024 * 1024);
    if (oversizedImage) return setError(`图片 ${oversizedImage.name} 不能超过 10 MB。`);
    const total = [...attachments.map((item) => item.file), ...incoming].reduce((sum, item) => sum + item.size, 0);
    if (total > generationUploadLimitBytes) return setError(`所有附件总大小不能超过 ${generationUploadLimitMb} MB。`);
    setAttachments((current) => [...current, ...incoming.map(attachmentFromFile)]);
    setUploadStatus({ phase: "selected", percent: 0, text: `已选择 ${nextCount} 个附件；点击生成后开始上传解析` });
  };

  const removeAttachment = (id: string) => {
    setAttachments((current) => {
      const target = current.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  };

  const updateAttachmentTitle = (id: string, title: string) => {
    setAttachments((current) => current.map((item) => item.id === id ? { ...item, title } : item));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    const validationError = validateSubmitDraft(draft, file, attachments, themeOptions, imageModeAvailable);
    if (validationError) {
      if (draft.sourceMode !== "pptx" && !draft.title.trim()) titleInputRef.current?.focus();
      window.alert(validationError);
      return setError(validationError);
    }
    if (draft.sourceMode !== "pptx") {
      try {
        const approved = await requestCreditSpend({
          operation: draft.generationMode === "image" ? "image2_generate" : "master_ai_generate",
          slideCount: Number(draft.pageCount) || 12,
        });
        if (!approved) return;
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "积分信息加载失败，请稍后重试");
        return;
      }
    }
    setBusy(true); setSeconds(0);
    try {
      const planning: Record<string, unknown> = {
        title: draftTitleForSubmit(draft, file),
        ...(draft.sourceMode === "pptx"
          ? { audience: "专业业务受众", scene: DEFAULT_PPT_SCENE }
          : {
              audienceMode: draft.audienceMode,
              audience: draft.audienceMode === "auto" ? "专业业务受众" : draft.audience.trim(),
              scene: draft.scene,
              sceneMode: draft.sceneMode,
              ...(draft.sceneMode === "custom" ? { customScene: draft.customScene.trim() } : {}),
            }),
        ...(draft.sourceMode === "pptx" ? {} : {
          pageCountMode: draft.pageCount ? "fixed" : "auto",
          pageCount: Number(draft.pageCount) || undefined,
        }),
        deliveryPurpose: draft.deliveryPurpose,
        narrativeMode: draft.narrativeMode, visualStyle: draft.visualStyle.trim() || undefined,
        tone: draft.tone.trim() || undefined,
        ...(isChinaEdition ? { aiModel: draft.aiModel } : {}),
        generationMode: draft.generationMode,
        ...(draft.generationMode === "image" ? {
          imageGenerationStrategy: draft.imageGenerationStrategy,
          imageStylePreference: { style: draft.imageStylePreference, strength: "prefer" },
        } : {}),
        requestedThemePack: selectedTheme?.key || draft.requestedThemePack,
        ...(webResearchAvailable && draft.webResearchMode === "live" ? { webResearchMode: "live" } : {}),
      };
      const uploadAttachments = draft.sourceMode === "file"
        ? attachments
        : [{ id: "pptx-source", file: file!, title: "" }];
      if (draft.sourceMode === "file") {
        if (draft.sourceText.trim()) planning.text = draft.sourceText.trim();
      } else {
        Object.assign(planning, {
          ...(draft.sourceMode === "pptx"
            ? {
                workflowMode: "existing_ppt_beautify",
                existingPptDesignMode: draft.existingPptDesignMode,
              }
            : {}),
        });
      }
      setUploadStatus({ phase: "uploading", percent: 1, text: "正在上传资料 1%" });
      const createTask = () => userApiWithUploadProgress<PptTaskDetail & { businessTask?: { id: string } }>(
        "/api/app/v2/ppt-tasks",
        businessTaskUploadForm({
          inputKind: draft.sourceMode,
          idempotencyKey: idempotencyKey.current,
          planning,
          async: true,
          attachments: uploadAttachments.map((attachment) => ({
            kind: isSupportedImageFile(attachment.file) ? "image" : "document",
            originalName: attachment.file.name,
            mimeType: mimeTypeForFile(attachment.file, draft.sourceMode),
            size: attachment.file.size,
            ...(isSupportedImageFile(attachment.file) && attachment.title.trim()
              ? { title: attachment.title.trim() }
              : {}),
          })),
        }, uploadAttachments.map((attachment) => attachment.file)),
        (progress, phase) => {
          setUploadStatus({
            phase,
            percent: progress,
            text: phase === "parsing" ? "资料已上传，正在校验并创建任务…" : `正在上传资料 ${progress}%`,
          });
        },
      );
      let result: PptTaskDetail & { businessTask?: { id: string } };
      try {
        result = await createTask();
      } catch (caught) {
        if (!(caught instanceof ApiError) || caught.code !== "sparse_source_confirmation_required") throw caught;
        const proceed = window.confirm(`${caught.message}\n\n继续后将改为智能页数，可能少于你选择的页数；系统不会用资料诊断、失败提示或无关内容凑页。`);
        if (!proceed) {
          setUploadStatus({ phase: "selected", percent: 0, text: "请补充文字、图片或文档后再生成" });
          return;
        }
        planning.sparseSourceConfirmed = true;
        delete planning.pageCount;
        planning.pageCountMode = "auto";
        setUploadStatus({ phase: "parsing", percent: 45, text: "已确认使用少量资料，正在按智能页数创建任务…" });
        result = await createTask();
      }
      const taskId = result.businessTask?.id || result.task?.id;
      if (!taskId) throw new Error("服务端未返回项目编号");
      localStorage.removeItem(DRAFT_KEY);
      sessionStorage.setItem(`pptdeep:task:${taskId}`, JSON.stringify(result));
      notifyCreditActionSubmitted();
      setUploadStatus({ phase: "parsed", percent: 100, text: "资料上传完成，正在进入生成页…" });
      navigate(projectGenerationPath(taskId, draft.generationMode));
    } catch (caught) {
      if (caught instanceof ApiError) idempotencyKey.current = clientId();
      if (caught instanceof ApiError && caught.code === "insufficient_credits") {
        notifyInsufficientCredits({ message: caught.message });
        setUploadStatus({ phase: "idle", percent: 0, text: "" });
        return;
      }
      setError(caught instanceof Error ? caught.message : "网页 PPT 生成失败，请稍后重试。");
      setUploadStatus({ phase: "error", percent: 0, text: caught instanceof Error ? caught.message : "上传或解析失败" });
    } finally { setBusy(false); }
  };

  return <div className="creation-page">
    <div className="creation-layout">
      <form id="new-ppt-form" className="creation-card" onSubmit={submit} aria-busy={busy}>
        <section className="creation-section creation-source-section" aria-labelledby="creation-source-title">
          <CreationSectionHeader index="01" title="内容来源" description={draft.sourceMode === "pptx" ? "上传需要美化的 PPTX，系统会保留逐页内容并重新设计。" : "上传业务资料，可补充文字内容，并在同一卡片内设置演示目标。"} id="creation-source-title"/>
          <div className={`source-choice ${isChinaEdition && !existingPptBeautifyAvailable ? "single-option" : ""}`}>
            <button type="button" className={draft.sourceMode === "file" ? "active" : ""} aria-pressed={draft.sourceMode === "file"} onClick={() => switchSource("file")}>
              <span className="source-choice-icon"><Icon name="files"/></span><span><strong>生成 PPT</strong><small>文档、表格、图片等资料</small></span><i aria-hidden="true"/>
            </button>
            {(!isChinaEdition || existingPptBeautifyAvailable) && <button type="button" className={draft.sourceMode === "pptx" ? "active" : ""} aria-pressed={draft.sourceMode === "pptx"} onClick={switchToExistingPptBeautify} disabled={!existingPptBeautifyAvailable}>
              <span className="source-choice-icon"><Icon name="plus"/></span><span><strong>美化现有 PPT</strong><small>{existingPptBeautifyAvailable ? "保留逐页内容并重新设计" : "当前账户暂未开放"}</small></span><i aria-hidden="true"/>
            </button>}
          </div>
          <div className="creation-source-body">
            {draft.sourceMode === "file" ? <section className="compact-source-composer" aria-label="上传资料与演示设置">
              <label className="compact-ppt-title-field">
                <span><strong>PPT 标题</strong><em>必填</em></span>
                <input
                  ref={titleInputRef}
                  value={draft.title}
                  onChange={(e) => update("title", e.target.value)}
                  maxLength={500}
                  required
                  aria-describedby="ppt-title-hint"
                  aria-invalid={Boolean(error.includes("PPT 标题") && !draft.title.trim())}
                  placeholder="请填写演示文稿的真实标题，例如：项目进展与下一步计划"
                />
                <small id="ppt-title-hint" className={error.includes("PPT 标题") && !draft.title.trim() ? "is-error" : ""}>
                  {error.includes("PPT 标题") && !draft.title.trim() ? "请输入 PPT 标题后再生成。" : "请根据汇报主题填写，不会使用上传文件名代替。"}
                </small>
              </label>
              <label className="compact-source-input">
                <textarea
                  value={draft.sourceText}
                  onChange={(e) => update("sourceText", e.target.value)}
                  minLength={FILE_SOURCE_TEXT_MIN_LENGTH}
                  maxLength={FILE_SOURCE_TEXT_MAX_LENGTH}
                  placeholder="描述演示主题或想法，或补充上传资料中未包含的背景、数据与关键结论…"
                  aria-label="资料内容（可选）"
                  aria-describedby="source-text-hint"
                />
                <small id="source-text-hint">留空即可；填写时至少 {FILE_SOURCE_TEXT_MIN_LENGTH} 字 · {draft.sourceText.length.toLocaleString()} / {FILE_SOURCE_TEXT_MAX_LENGTH.toLocaleString()} 字符</small>
              </label>
              <div className="compact-source-toolbar">
                <AttachmentUploadButton accept={SOURCE_FILE_ACCEPT} label="添加资料文件" hint={`${SOURCE_FILE_LABEL} · 最多 10 个 · 总计不超过 ${generationUploadLimitMb} MB`} busy={busy} onChoose={chooseAttachments}/>
              </div>
              {showUploadProgress && <UploadProgress status={uploadStatus}/>}
              <div className={`form-grid creation-fields compact-source-fields ${isChinaEdition ? "is-china-edition" : ""}`}>
                {isChinaEdition && <label>AI模型<select value={draft.aiModel} onChange={(e) => update("aiModel", e.target.value as ChinaAiModel)}>{CHINA_AI_MODEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>}
                <label>目标受众<select value={pptAudienceSelectValue(draft.audienceMode, draft.audience)} onChange={(e) => selectAudience(e.target.value)}>{PPT_AUDIENCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                <label>期望页数<select value={draft.pageCount} onChange={(e) => update("pageCount", e.target.value)}>{PAGE_COUNT_OPTIONS.map((option) => <option key={option.value || "auto"} value={option.value}>{option.label}</option>)}</select></label>
                <label>表达语气<select value={draft.tone} onChange={(e) => update("tone", e.target.value)}>{TONE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
                <label>使用场景<select value={pptSceneSelectValue(draft.sceneMode, draft.scene)} onChange={(e) => selectScene(e.target.value)}>{PPT_SCENE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                {draft.audienceMode === "custom" && <label className="compact-context-custom">自定义目标受众<input value={draft.audience} onChange={(e) => update("audience", e.target.value)} maxLength={1_000} placeholder="例如：市文旅局项目评审与分管领导" autoFocus/></label>}
                {draft.sceneMode === "custom" && <label className="compact-context-custom">自定义使用场景<input value={draft.customScene} onChange={(e) => update("customScene", e.target.value)} maxLength={1_000} placeholder="例如：文旅项目试点合作专题汇报" autoFocus={draft.audienceMode !== "custom"}/></label>}
              </div>
              {webResearchAvailable && <label className="tester-toggle web-research-tester-toggle">
                <input type="checkbox" checked={draft.webResearchMode === "live"} onChange={(event) => update("webResearchMode", event.target.checked ? "live" : "disabled")}/>
                <span>
                  <strong>联网证据增强（Tester）</strong>
                  <small>只补强政策、市场、本地适配和公开案例；来源原文核验通过后才进入大纲，最多影响 4 个核心页面。</small>
                  <small className="web-research-cost-estimate">当前案例实测估算：输入 token 约 +100%～160%，规划耗时约 +45%～80%；不同主题和来源响应会波动。</small>
                </span>
              </label>}
              <AttachmentList attachments={attachments} busy={busy} onRemove={removeAttachment} onTitle={updateAttachmentTitle}/>
            </section> : <section className={`compact-source-composer compact-pptx-composer upload-${uploadStatus.phase}`} aria-label="选择 PPTX 与演示设置">
              <div className="compact-source-toolbar compact-pptx-toolbar">
                <label className="compact-pptx-button">
                  <input
                    type="file"
                    accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    onChange={(event) => {
                      chooseFile(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                    disabled={busy}
                  />
                  <Icon name="files"/>
                  <span>{file ? "重新选择 PPTX" : "选择 PPTX"}</span>
                </label>
                <span className="compact-pptx-file">
                  <strong>{file?.name || "未选择文件"}</strong>
                  <small>{uploadStatus.text || (file ? `${fileTypeText(file, draft.sourceMode)} · ${(file.size / 1024 / 1024).toFixed(2)} MB · 已通过本地校验` : "仅支持 PPTX · 最大 30 MB")}</small>
                </span>
              </div>
              {showUploadProgress && <UploadProgress status={uploadStatus}/>}
              {file && <div className="existing-ppt-settings">
                <label className="existing-ppt-title-field">
                  <span>PPT 标题</span>
                  <input value={draft.title} onChange={(e) => update("title", e.target.value)} maxLength={500} placeholder="请输入 PPT 标题"/>
                  <small>已根据文件名自动填写，你可以在开始美化前修改。</small>
                </label>
                <ExistingPptDesignModePicker value={draft.existingPptDesignMode} onChange={(value) => update("existingPptDesignMode", value)}/>
                <ThemePicker
                  options={themeOptions}
                  selectedKey={selectedTheme?.key || draft.requestedThemePack}
                  title="风格模板"
                  description="默认智能选择；也可以指定整套演示的配色、字体和版式语言。"
                  onChange={(value) => update("requestedThemePack", value)}
                />
                {testerMode && <section className="image-style-preference-picker existing-ppt-illustration-style" aria-labelledby="existing-ppt-illustration-style-title">
                  <div className="theme-picker-head"><div><strong id="existing-ppt-illustration-style-title">插画风格</strong><span>Tester 专用，用于验证新增图片与插画的统一媒介。</span></div><small>已选：{imageStylePreferenceName(draft.imageStylePreference)}</small></div>
                  <label>
                    <span>新增图片与插画的统一媒介</span>
                    <select value={draft.imageStylePreference} onChange={(event) => update("imageStylePreference", event.target.value as ImageStylePreferenceStyle)}>
                      {IMAGE_STYLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label} · {option.description}</option>)}
                    </select>
                  </label>
                </section>}
              </div>}
            </section>}
          </div>
        </section>

        {draft.sourceMode !== "pptx" && <section className="creation-section creation-design-section" aria-labelledby="creation-design-title">
          <CreationSectionHeader index="02" title="视觉方案" description="生成模式和全部主题都在当前区域直接选择。" id="creation-design-title"/>
          <section className="generation-mode-picker" aria-label="生成模式">
            <div>{generationModeOptions.map((mode) => <label key={mode.key} className={draft.generationMode === mode.key ? "selected" : ""}>
              <input type="radio" name="generation-mode" value={mode.key} checked={draft.generationMode === mode.key} disabled={!mode.available} onChange={() => switchGenerationMode(mode.key)}/>
              <span><b>{mode.name}</b><small>{mode.available ? mode.description : `${mode.description} · 当前账户暂未开放`}</small></span>
            </label>)}</div>
          </section>
          {import.meta.env.DEV && draft.generationMode === "image" && <section className="image-generation-strategy-picker" aria-labelledby="image-generation-strategy-title">
            <div className="theme-picker-head"><div><strong id="image-generation-strategy-title">图片生成方式</strong><span>仅开发模式可选，用于验证不同的图片生成流程。</span></div><small>已选：{imageGenerationStrategyName(draft.imageGenerationStrategy)}</small></div>
            <div>{IMAGE_GENERATION_STRATEGY_OPTIONS.map((strategy) => <label key={strategy.key} className={draft.imageGenerationStrategy === strategy.key ? "selected" : ""}>
              <input type="radio" name="image-generation-strategy" value={strategy.key} checked={draft.imageGenerationStrategy === strategy.key} onChange={() => update("imageGenerationStrategy", strategy.key)}/>
              <span><b>{strategy.name}</b><small>{strategy.description}</small></span>
            </label>)}</div>
          </section>}
          {draft.generationMode === "image" && <section className="image-style-preference-picker" aria-labelledby="image-style-preference-title">
            <div className="theme-picker-head"><div><strong id="image-style-preference-title">图片风格</strong><span>默认写实优先；特殊媒介仅在你明确选择时使用。</span></div><small>已选：{imageStylePreferenceName(draft.imageStylePreference)}</small></div>
            <label>
              <span>新增图片与插画的统一媒介</span>
              <select value={draft.imageStylePreference} onChange={(event) => update("imageStylePreference", event.target.value as ImageStylePreferenceStyle)}>
                {IMAGE_STYLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label} · {option.description}</option>)}
              </select>
            </label>
          </section>}
          <ThemePicker options={themeOptions} selectedKey={selectedTheme?.key || draft.requestedThemePack} onChange={(value) => update("requestedThemePack", value)}/>
        </section>}

        {error && <div className="form-error" role="alert">{error}</div>}
        {busy && <div className="planning-progress" aria-live="polite"><i/><div><strong>{draft.sourceMode === "pptx" ? "正在提交美化任务" : "正在提交生成任务"}</strong><span>已用时 {seconds} 秒，提交成功后会进入项目页自动刷新进度。</span></div></div>}
        <div className={`creation-footer primary-action-footer ${draft.sourceMode === "pptx" ? "beautify-footer" : "generate-footer"}`}><span><i/>所有设置已自动保存在当前浏览器</span><Button type="submit" disabled={busy}>{busy ? "正在提交…" : draft.sourceMode === "pptx" ? "立即开始美化" : "立即开始生成"}</Button></div>
      </form>

    </div>
  </div>;
}

function CreationSectionHeader({ index, title, description, id }: { index: string; title: string; description: string; id: string }) {
  return <header className="creation-section-head"><span aria-hidden="true">{index}</span><div><h2 id={id}>{title}</h2><p>{description}</p></div></header>;
}

function AttachmentUploadButton({
  accept,
  label,
  hint,
  busy,
  onChoose,
}: {
  accept: string;
  label: string;
  hint: string;
  busy: boolean;
  onChoose: (files: FileList | null) => void;
}) {
  return <label className={`compact-attachment-trigger${busy ? " is-disabled" : ""}`} aria-disabled={busy}>
    <input type="file" multiple accept={accept} onChange={(event) => { onChoose(event.target.files); event.target.value = ""; }} disabled={busy}/>
    <span className="compact-attachment-icon"><Icon name="files"/></span>
    <span className="compact-attachment-copy"><strong>{label}</strong><small>{hint}</small></span>
    <span className="compact-attachment-action" aria-hidden="true">{busy ? "处理中…" : "选择文件"}</span>
  </label>;
}

function AttachmentList({
  attachments,
  busy,
  onRemove,
  onTitle,
}: {
  attachments: DraftAttachment[];
  busy: boolean;
  onRemove: (id: string) => void;
  onTitle: (id: string, title: string) => void;
}) {
  if (!attachments.length) return null;
  return <div className="attachment-list compact-attachment-list">{attachments.map((attachment) => {
      const image = isSupportedImageFile(attachment.file);
      return <article key={attachment.id} className="attachment-item">
        {image && attachment.previewUrl ? <img src={attachment.previewUrl} alt={attachment.title || attachment.file.name}/> : <span className="attachment-file-icon">{sourceFileType(attachment.file.name)?.label || "文件"}</span>}
        <div><strong>{attachment.file.name}</strong><small>{(attachment.file.size / 1024 / 1024).toFixed(2)} MB</small>{image && <input value={attachment.title} onChange={(event) => onTitle(attachment.id, event.target.value)} maxLength={500} placeholder="图片标题（可选）"/>}</div>
        <button type="button" onClick={() => onRemove(attachment.id)} disabled={busy} aria-label={`删除 ${attachment.file.name}`}>删除</button>
      </article>;
    })}</div>;
}

function ExistingPptDesignModePicker({ value, onChange }: { value: ExistingPptDesignMode; onChange: (value: ExistingPptDesignMode) => void }) {
  return <section className="existing-ppt-design-mode-picker" aria-labelledby="existing-ppt-design-mode-title">
    <div className="theme-picker-head"><div><strong id="existing-ppt-design-mode-title">PPT 美化方式</strong><span>选择系统重新设计原稿时，对原版式和素材的保留程度。</span></div><small>已选：{existingPptDesignModeName(value)}</small></div>
    <div>{EXISTING_PPT_DESIGN_MODE_OPTIONS.map((option) => <label key={option.value} className={value === option.value ? "selected" : ""}>
      <input type="radio" name="existing-ppt-design-mode" value={option.value} checked={value === option.value} onChange={() => onChange(option.value)}/>
      <span><b>{option.label}</b><small>{option.description}</small></span>
    </label>)}</div>
  </section>;
}

function ThemePicker({
  options,
  selectedKey,
  onChange,
  title = "选择主题",
  description = "主题会统一整套演示的配色、字体和版式语言。",
}: {
  options: ThemeOption[];
  selectedKey: string;
  onChange: (value: string) => void;
  title?: string;
  description?: string;
}) {
  const selected = options.find((theme) => theme.key === selectedKey) || options[0];
  const titleId = title === "风格模板" ? "existing-ppt-theme-picker-title" : "theme-picker-title";
  return <section className="theme-picker" aria-labelledby={titleId}>
    <div className="theme-picker-head"><div><strong id={titleId}>{title}</strong><span>{description}</span></div><small>已选：{selected?.name}</small></div>
    <div className="theme-option-grid">{options.map((theme, index) => {
      const catalogStyle = theme.mode === "image" ? marketingStyleForCode(theme.key) : undefined;
      return <label key={theme.key} className={selected?.key === theme.key ? "selected" : ""}>
        <input type="radio" name="requested-theme-pack" value={theme.key} aria-label={`${theme.name}：${theme.description}`} checked={selected?.key === theme.key} onChange={() => onChange(theme.key)}/>
        <span className={`theme-option-preview ${theme.previewImageUrl ? "" : catalogStyle ? "template-card image-theme-catalog-preview" : `theme-option-${theme.previewThemePack}`}`}>
          {theme.previewImageUrl
            ? <img src={theme.previewImageUrl} alt={`${theme.name}主题示例`} loading={selected?.key === theme.key ? "eager" : "lazy"} decoding="async" draggable={false} onError={(event) => { event.currentTarget.hidden = true; }}/>
            : catalogStyle
              ? <StylePreview style={catalogStyle}/>
              : <><img src={THEME_PREVIEW_IMAGES[theme.previewThemePack]} alt={`${theme.name}主题示例`} loading={selected?.key === theme.key ? "eager" : "lazy"} decoding="async" draggable={false} onError={(event) => { event.currentTarget.hidden = true; }}/><i aria-hidden="true">{String(index + 1).padStart(2, "0")}</i></>}
        </span>
        <span className="theme-option-copy"><b>{theme.name}{theme.recommended ? <em>推荐</em> : null}</b><small>{theme.description}</small></span>
      </label>;
    })}</div>
    <p className="theme-scroll-hint">当前共 {options.length} 个主题，点击卡片即可选择</p>
  </section>;
}

function loadDraft(searchParams?: URLSearchParams): NewPptDraft {
  try {
    const currentDraftValue = localStorage.getItem(DRAFT_KEY);
    const persistedValue = currentDraftValue
      || LEGACY_DRAFT_KEYS.map((key) => localStorage.getItem(key)).find(Boolean)
      || "{}";
    const migratedLegacyDraft = !currentDraftValue;
    const persisted = JSON.parse(persistedValue) as Record<string, unknown>;
    const {
      goal: _discardedLegacyGoal,
      text: legacyText,
      sourceText: persistedSourceText,
      ...persistedWithoutLegacyFields
    } = persisted;
    const stored = {
      ...initialDraft,
      ...persistedWithoutLegacyFields,
      sourceText: normalizeFileSourceText(persistedSourceText ?? legacyText),
    } as NewPptDraft;
    const requestedSource = searchParams?.get("source");
    const sourceMode: NewPptDraft["sourceMode"] = requestedSource === "pptx"
      ? "pptx"
      : requestedSource === "docx" || requestedSource === "file" || requestedSource === "text"
        ? "file"
        : normalizeSourceMode(stored.sourceMode);
    const requestedGeneration = searchParams?.get("generation");
    // A fresh visit always starts from standard mode. Persisted drafts may keep
    // form content, but must not silently carry the previous project's renderer.
    const generationCandidate = requestedGeneration || "standard";
    const generationMode = sourceMode === "pptx"
      ? "image"
      : isGenerationMode(generationCandidate) && generationCandidate !== "html"
        ? generationCandidate
        : "standard";
    const options = themeOptionsForMode(generationMode, sourceMode);
    const requestedTheme = searchParams?.get("theme") || stored.requestedThemePack;
    const requestedNarrative = searchParams?.get("narrative");
    const narrativeMode = isNarrativeMode(requestedNarrative) ? requestedNarrative : stored.narrativeMode;
    const storedPageCount = isPageCountOption(stored.pageCount) ? stored.pageCount : initialDraft.pageCount;
    const requestedPages = searchParams?.has("pages") ? searchParams.get("pages") || "" : storedPageCount;
    const pageCount = isPageCountOption(requestedPages) ? requestedPages : storedPageCount;
    const requestedAudience = normalizePptAudienceSelection(
      searchParams?.has("audience") ? searchParams.get("audience") : stored.audience,
      searchParams?.has("audienceMode")
        ? searchParams.get("audienceMode")
        : migratedLegacyDraft ? "auto" : persisted.audienceMode,
    );
    const requestedScene = normalizePptSceneSelection(
      searchParams?.has("scene") ? searchParams.get("scene") : stored.scene,
      searchParams?.has("sceneMode")
        ? searchParams.get("sceneMode")
        : migratedLegacyDraft ? "auto" : persisted.sceneMode,
      searchParams?.has("customScene") ? searchParams.get("customScene") : stored.customScene,
    );
    return {
      ...stored,
      sourceMode,
      originalName: "",
      sourceText: searchParams?.has("text")
        ? normalizeFileSourceText(searchParams.get("text"))
        : normalizeFileSourceText(stored.sourceText),
      title: searchParams?.has("title")
        ? (searchParams.get("title") || "").slice(0, 500)
        : migratedLegacyDraft && sourceMode === "file" ? "" : stored.title,
      audienceMode: requestedAudience.mode,
      audience: requestedAudience.audience,
      tone: normalizeTone(stored.tone),
      aiModel: normalizeChinaAiModel(searchParams?.has("aiModel") ? searchParams.get("aiModel") : stored.aiModel),
      sceneMode: requestedScene.mode,
      scene: requestedScene.scene,
      customScene: requestedScene.customScene,
      narrativeMode,
      pageCount,
      generationMode,
      existingPptDesignMode: isExistingPptDesignMode(stored.existingPptDesignMode)
        ? stored.existingPptDesignMode
        : "enhanced",
      existingPptImageResolution: "auto",
      imageGenerationStrategy: import.meta.env.DEV && isImageGenerationStrategy(stored.imageGenerationStrategy)
        ? stored.imageGenerationStrategy
        : DEFAULT_IMAGE_GENERATION_STRATEGY,
      imageStylePreference: isImageStylePreferenceStyle(stored.imageStylePreference)
        ? stored.imageStylePreference
        : "auto",
      webResearchMode: stored.webResearchMode === "live" ? "live" : "disabled",
      requestedThemePack: options.some((theme) => theme.key === requestedTheme)
        ? requestedTheme
        : defaultThemeKey(generationMode, options, initialDraft.requestedThemePack),
    };
  }
  catch { return initialDraft; }
}

function attachmentFromFile(file: File): DraftAttachment {
  return {
    id: clientId(),
    file,
    title: "",
    ...(isSupportedImageFile(file) ? { previewUrl: URL.createObjectURL(file) } : {}),
  };
}

function clearAttachments(attachments: DraftAttachment[]): void {
  attachments.forEach((attachment) => {
    if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
  });
}

export function businessTaskUploadForm(request: Record<string, unknown>, files: File[]): FormData {
  const form = new FormData();
  form.append("request", JSON.stringify(request));
  files.forEach((file, index) => form.append(`file-${index}`, file, file.name));
  return form;
}

function UploadProgress({ status }: { status: UploadStatus }) {
  return <div
    className={`compact-upload-progress upload-${status.phase}`}
    role="progressbar"
    aria-label={status.text || "资料上传进度"}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={status.percent}
  >
    <div><span>{status.text}</span><strong>{status.percent}%</strong></div>
    <i className="upload-progress compact-source-progress" aria-hidden="true"><b style={{ width: `${status.percent}%` }}/></i>
  </div>;
}

function draftWithSourceMode(draft: NewPptDraft, sourceMode: NewPptDraft["sourceMode"]): NewPptDraft {
  if (sourceMode !== "pptx") {
    const options = themeOptionsForMode(draft.generationMode, sourceMode);
    return {
      ...draft,
      sourceMode,
      originalName: "",
      requestedThemePack: options.some((theme) => theme.key === draft.requestedThemePack)
        ? draft.requestedThemePack
        : defaultThemeKey(draft.generationMode, options, initialDraft.requestedThemePack),
    };
  }
  const generationMode = "image";
  const themes = themeOptionsForMode(generationMode, "pptx");
  const requestedThemePack = defaultThemeKey(generationMode, themes, draft.requestedThemePack);
  return {
    ...draft,
    sourceMode,
    originalName: "",
    generationMode,
    imageGenerationStrategy: DEFAULT_IMAGE_GENERATION_STRATEGY,
    existingPptDesignMode: isExistingPptDesignMode(draft.existingPptDesignMode)
      ? draft.existingPptDesignMode
      : "enhanced",
    existingPptImageResolution: "auto",
    requestedThemePack,
  };
}

function draftWithExistingPptBeautify(draft: NewPptDraft): NewPptDraft {
  const pptxDraft = draftWithSourceMode(draft, "pptx");
  const options = themeOptionsForMode("image", "pptx");
  return {
    ...pptxDraft,
    generationMode: "image",
    requestedThemePack: options.some((theme) => theme.key === pptxDraft.requestedThemePack)
      ? pptxDraft.requestedThemePack
      : defaultThemeKey("image", options, pptxDraft.requestedThemePack),
    imageGenerationStrategy: DEFAULT_IMAGE_GENERATION_STRATEGY,
  };
}

export function validateSubmitDraft(
  draft: NewPptDraft,
  file: File | undefined,
  attachments: DraftAttachment[],
  themeOptions: Array<{ key: string }>,
  imageModeAvailable = true,
): string {
  if (draft.generationMode === "image" && !imageModeAvailable) return "图片模式当前未向此账户开放。";
  const missing: string[] = [];
  if (draft.sourceMode === "pptx" ? !file : !attachments.length) {
    missing.push(draft.sourceMode === "pptx" ? "PPTX 文件" : "资料文件");
  }
  if (!draft.title.trim() && !(draft.sourceMode === "pptx" && file)) missing.push("PPT 标题");
  if (draft.sourceMode !== "pptx" && draft.audienceMode !== "auto" && !draft.audience.trim()) missing.push("目标受众");
  if (draft.sourceMode !== "pptx" && draft.sceneMode === "custom" && !draft.customScene.trim()) missing.push("使用场景");
  if (draft.sourceMode !== "pptx" && (!isGenerationMode(draft.generationMode) || draft.generationMode === "html")) missing.push("生成模式");
  if (draft.sourceMode !== "pptx" && (!draft.requestedThemePack.trim() || !themeOptions.some((theme) => theme.key === draft.requestedThemePack))) missing.push("主题");
  if (missing.length) return `请补全：${missing.join("、")}。`;
  if (draft.sourceMode === "file") {
    const textError = validateFileSourceTextLength(draft.sourceText);
    if (textError) return textError;
  }
  return "";
}

export function userApiWithUploadProgress<T>(
  path: string,
  body: FormData,
  onProgress: (percent: number, phase: "uploading" | "parsing") => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startedAt = performance.now();
    xhr.open("POST", path);
    const token = localStorage.getItem("pptdeep_user_token") || localStorage.getItem("df_ai_ppt_token") || "";
    if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress(Math.max(1, Math.min(94, Math.round((event.loaded / event.total) * 94))), "uploading");
    };
    xhr.upload.onload = () => onProgress(96, "parsing");
    xhr.onload = () => {
      const data = parseJsonResponse(xhr.responseText);
      debugFrontendXhrRequest({
        method: "POST",
        url: path,
        body: { multipart: true, fileCount: [...body.keys()].filter((key) => key.startsWith("file-")).length },
        status: xhr.status,
        result: data,
        durationMs: Math.round((performance.now() - startedAt) * 10) / 10,
      });
      if (xhr.status < 200 || xhr.status >= 300 || data.ok === false) {
        reject(new ApiError(xhr.status, String(data.error || "请求失败"), typeof data.code === "string" ? data.code : undefined, xhr.getResponseHeader("x-request-id") || undefined));
        return;
      }
      resolve(data as T);
    };
    xhr.onerror = () => {
      debugFrontendXhrRequest({
        method: "POST",
        url: path,
        body: { multipart: true },
        error: "network_error",
        durationMs: Math.round((performance.now() - startedAt) * 10) / 10,
      });
      reject(new Error("上传资料失败，请检查网络后重试。"));
    };
    xhr.send(body);
  });
}

function draftTitleForSubmit(draft: NewPptDraft, file?: File): string {
  return draft.title.trim() || (draft.sourceMode === "pptx" && file ? fileTitle(file.name) : "");
}

function parseJsonResponse(text: string): Record<string, unknown> {
  try { return JSON.parse(text || "{}") as Record<string, unknown>; }
  catch { return {}; }
}

function fileTypeText(file: File, sourceMode: InputKind): string {
  if (sourceMode === "pptx") return "PPTX";
  return sourceFileType(file.name)?.label || "资料文件";
}

function normalizeSourceMode(value: unknown): NewPptDraft["sourceMode"] {
  return value === "pptx" ? "pptx" : "file";
}

function isGenerationMode(value: unknown): value is GenerationMode {
  return value === "standard" || value === "html" || value === "image";
}

function isImageGenerationStrategy(value: unknown): value is ImageGenerationStrategy {
  return value === "batch_preview" || value === "per_slide_hd";
}

function isExistingPptDesignMode(value: unknown): value is ExistingPptDesignMode {
  return value === "enhanced" || value === "premium" || value === "faithful";
}

function isImageStylePreferenceStyle(value: unknown): value is ImageStylePreferenceStyle {
  return IMAGE_STYLE_OPTIONS.some((option) => option.value === value);
}

function defaultThemeKey(
  _generationMode: GenerationMode,
  options: Array<{ key: string }>,
  fallback: string,
): string {
  return options[0]?.key || fallback;
}

function isNarrativeMode(value: unknown): value is NewPptDraft["narrativeMode"] {
  return value === "briefing" || value === "pyramid" || value === "narrative" || value === "instructional" || value === "showcase";
}

function isPageCountOption(value: unknown): value is string {
  return typeof value === "string" && PAGE_COUNT_OPTIONS.some((option) => option.value === value);
}

function normalizeTone(value: unknown): string {
  return typeof value === "string" && TONE_OPTIONS.includes(value as (typeof TONE_OPTIONS)[number])
    ? value
    : TONE_OPTIONS[0];
}

function modeName(value: GenerationMode) {
  return GENERATION_MODE_OPTIONS.find((mode) => mode.key === value)?.name || "标准模式";
}

function existingPptDesignModeName(value: ExistingPptDesignMode) {
  return EXISTING_PPT_DESIGN_MODE_OPTIONS.find((option) => option.value === value)?.label || "高质量增强";
}

function imageGenerationStrategyName(value: ImageGenerationStrategy) {
  return IMAGE_GENERATION_STRATEGY_OPTIONS.find((strategy) => strategy.key === value)?.name || "主图高清";
}

function imageStylePreferenceName(value: ImageStylePreferenceStyle) {
  return IMAGE_STYLE_OPTIONS.find((option) => option.value === value)?.label || "自动（写实优先）";
}
